import { useCallback, useMemo, useState } from "react";
import type {
  DataTableFilterConfig,
  DataTablePaginationMeta,
  DataTableSort,
  DataTableState,
} from "@/components/shared/types/data-table";
import { DEFAULT_PAGE_SIZE } from "@/components/shared/types/data-table";

export type DataTableMode = "client" | "server";

export interface UseDataTableOptions<T> {
  mode?: DataTableMode;
  data?: T[];
  totalCount?: number;
  pageSize?: number;
  initialSearch?: string;
  initialSort?: DataTableSort;
  initialFilters?: Record<string, string>;
  filterConfigs?: DataTableFilterConfig[];
  /** Keys used for client-side search */
  searchKeys?: (keyof T)[];
  /** Custom client-side filter predicate */
  filterPredicate?: (row: T, state: DataTableState) => boolean;
  /** Called whenever table state changes (useful for server-side fetching) */
  onStateChange?: (state: DataTableState) => void;
}

function getNestedValue<T>(row: T, key: keyof T | string): unknown {
  return row[key as keyof T];
}

function compareValues(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

export function useDataTable<T>({
  mode = "client",
  data = [],
  totalCount: serverTotalCount,
  pageSize: initialPageSize = DEFAULT_PAGE_SIZE,
  initialSearch = "",
  initialSort,
  initialFilters = {},
  filterConfigs = [],
  searchKeys = [],
  filterPredicate,
  onStateChange,
}: UseDataTableOptions<T>) {
  const [state, setState] = useState<DataTableState>({
    page: 1,
    pageSize: initialPageSize,
    search: initialSearch,
    sort: initialSort,
    filters: filterConfigs.reduce<Record<string, string>>((acc, filter) => {
      acc[filter.id] = initialFilters[filter.id] ?? "all";
      return acc;
    }, { ...initialFilters }),
  });

  const updateState = useCallback(
    (patch: Partial<DataTableState>) => {
      setState((prev) => {
        const next = { ...prev, ...patch };
        onStateChange?.(next);
        return next;
      });
    },
    [onStateChange]
  );

  const setPage = useCallback((page: number) => updateState({ page }), [updateState]);
  const setPageSize = useCallback(
    (pageSize: number) => updateState({ page: 1, pageSize }),
    [updateState]
  );
  const setSearch = useCallback(
    (search: string) => updateState({ page: 1, search }),
    [updateState]
  );
  const setSort = useCallback(
    (sort?: DataTableSort) => updateState({ page: 1, sort }),
    [updateState]
  );
  const setFilter = useCallback(
    (id: string, value: string) => {
      setState((prev) => {
        const next = {
          ...prev,
          page: 1,
          filters: { ...prev.filters, [id]: value },
        };
        onStateChange?.(next);
        return next;
      });
    },
    [onStateChange]
  );

  const toggleSort = useCallback(
    (columnId: string) => {
      if (state.sort?.id === columnId) {
        if (state.sort.direction === "asc") {
          setSort({ id: columnId, direction: "desc" });
        } else {
          setSort(undefined);
        }
      } else {
        setSort({ id: columnId, direction: "asc" });
      }
    },
    [setSort, state.sort]
  );

  const processed = useMemo(() => {
    if (mode === "server") {
      return {
        rows: data,
        totalCount: serverTotalCount ?? data.length,
      };
    }

    let rows = [...data];

    if (state.search.trim()) {
      const query = state.search.trim().toLowerCase();
      rows = rows.filter((row) =>
        searchKeys.some((key) =>
          String(getNestedValue(row, key) ?? "")
            .toLowerCase()
            .includes(query)
        )
      );
    }

    if (filterPredicate) {
      rows = rows.filter((row) => filterPredicate(row, state));
    } else {
      Object.entries(state.filters).forEach(([key, value]) => {
        if (!value || value === "all") return;
        rows = rows.filter((row) => String(getNestedValue(row, key as keyof T)) === value);
      });
    }

    if (state.sort) {
      const { id, direction } = state.sort;
      rows.sort((a, b) => {
        const result = compareValues(getNestedValue(a, id), getNestedValue(b, id));
        return direction === "asc" ? result : -result;
      });
    }

    return { rows, totalCount: rows.length };
  }, [data, filterPredicate, mode, searchKeys, serverTotalCount, state]);

  const pagination = useMemo<DataTablePaginationMeta>(() => {
    const totalCount = processed.totalCount;
    const totalPages = Math.max(1, Math.ceil(totalCount / state.pageSize));
    const page = Math.min(state.page, totalPages);
    const from = totalCount === 0 ? 0 : (page - 1) * state.pageSize + 1;
    const to = Math.min(page * state.pageSize, totalCount);

    return { page, pageSize: state.pageSize, totalCount, totalPages, from, to };
  }, [processed.totalCount, state.page, state.pageSize]);

  const pageRows = useMemo(() => {
    if (mode === "server") return processed.rows;
    const start = (pagination.page - 1) * pagination.pageSize;
    return processed.rows.slice(start, start + pagination.pageSize);
  }, [mode, pagination.page, pagination.pageSize, processed.rows]);

  return {
    state,
    rows: pageRows,
    /** Every row after search/filter/sort (not just the current page); use for exports. */
    allRows: processed.rows,
    pagination,
    setPage,
    setPageSize,
    setSearch,
    setSort,
    setFilter,
    toggleSort,
  };
}
